import { useEffect } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal, RevealImage } from "@/components/motion";
import Marquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/data/content";

const TIMELINE = [
  {
    year: "Year 5",
    title: "Where it began",
    text: "I started dieting when I was 10. I wanted to be smaller, to be more valued. It became a cycle of intense dieting and exercise followed by binge eating, unhelpful rules and confusion, second guessing every eating and exercise choice I made.",
  },
  {
    year: "June 2017",
    title: "The turning point",
    text: "I was skiing at Mt Buller in year 12 when one of the gym staff started talking to me about strength, and what it took her to feel good in her body again. That one conversation changed my future. I didn't look back.",
  },
  {
    year: "The years since",
    title: "From smaller to stronger",
    text: "I worked with coaches to let go of restrictive measures and build stronger intuition. I found powerlifting, no longer choosing to be smaller through exercise, but faster and more powerful. Taking up space.",
  },
  {
    year: "2021",
    title: "The Strength Movement is born",
    text: "My journey inspired my studies in sports science and disordered eating practice, and the launch of my own coaching business. Today, TSM helps women build autonomy in their choices and discover what building their body's capacity looks like without sacrificing their health.",
  },
];

const CREDENTIALS = [
  "Bachelor of Exercise and Sport Science",
  "Studies in eating disorder psychology",
  "ACFEB Body Image Program facilitator",
];

const About = () => {

  return (
    <main data-testid="about-page">
      {/* HEADER */}
      <section className="bg-mist grain">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <div className="grid items-end gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="label-text text-ocean" data-testid="about-eyebrow">About Me</p>
              <h1
                className="mt-6 font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight"
                data-testid="about-heading"
              >
                <MaskedLines lines={["Hey, my", "name's Kim."]} />
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/70"
              >
                Founder and head coach of The Strength Movement, started in 2021,
                in the midst of finding myself on the other side of second
                guessing my eating and exercise behaviours, and navigating
                plateaus in the gym.
              </motion.p>
            </div>
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="arch-clip overflow-hidden shadow-2xl shadow-ink/10 max-h-[80vh] md:max-h-none"
                data-testid="about-hero-image"
              >
                <img
                  src="/images/community/kim-plate.webp"
                  alt="Coach Kim, founder and head coach of The Strength Movement"
                  className="h-full w-full object-cover"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Marquee dark={false} />

      {/* MISSION */}
      <section className="bg-white" data-testid="mission-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <Reveal>
            <p className="max-w-4xl font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
              Through studies in exercise science and disordered eating, it became
              my mission to help females navigate the confusion,
              <span className="italic font-light text-ocean"> build more autonomy in their choices, </span>
              and bridge the gaps between wellness and performance.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STORY TIMELINE */}
      <section className="bg-mist" data-testid="story-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <Reveal>
            <p className="label-text text-ocean">My story</p>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Why I'm <span className="italic font-light text-ink/50">here.</span>
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="relative border-l border-ink/15 pl-8 md:pl-12 space-y-16">
                {TIMELINE.map((t, i) => (
                  <Reveal key={t.year} delay={i * 0.05}>
                    <div className="relative" data-testid={`story-chapter-${i}`}>
                      <span className="absolute -left-[41px] md:-left-[57px] top-1.5 h-3 w-3 rounded-full bg-ocean ring-4 ring-mist" />
                      <p className="label-text text-ocean">{t.year}</p>
                      <h3 className="mt-3 font-display text-3xl md:text-4xl">{t.title}</h3>
                      <p className="mt-4 max-w-lg text-sm md:text-base leading-relaxed text-ink/70">{t.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-32 space-y-6">
                <RevealImage
                  src={IMAGES.kimEveryday}
                  alt="Kim outdoors, strength beyond the gym"
                  className="aspect-[4/5]"
                  testId="story-image"
                />
                <Reveal delay={0.1}>
                  <blockquote className="border border-ink/10 bg-white p-8" data-testid="story-quote">
                    <p className="font-display text-2xl italic font-light leading-snug text-ink/80">
                      “Strength is the vehicle. The destination is trust.”
                    </p>
                  </blockquote>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="bg-ink text-white grain" data-testid="credentials-section">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="label-text text-ocean">Qualifications</p>
                <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-[0.95] tracking-tight text-balance">
                  The study behind
                  <span className="italic font-light text-white/60"> the coaching.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="divide-y divide-white/10 border-y border-white/10">
                {CREDENTIALS.map((c, i) => (
                  <Reveal key={c} delay={i * 0.06} y={14}>
                    <li className="flex items-center gap-4 py-6" data-testid={`credential-${i}`}>
                      <BadgeCheck size={20} strokeWidth={1.5} className="shrink-0 text-ocean" />
                      <span className="text-base md:text-lg text-white/85">{c}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
              <Reveal delay={0.2}>
                <p className="mt-10 max-w-xl font-display text-2xl italic font-light leading-snug text-white/75">
                  “The Strength Movement is expanding, and I want you to be a
                  part of it.”
                </p>
                <a
                  href="https://www.instagram.com/the_strength.movement/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-instagram-link"
                  className="group mt-6 inline-flex items-center gap-2 text-sm font-bold text-white transition-colors hover:text-ocean"
                >
                  Follow the journey on Instagram
                  <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="Sound like the coach"
        accent="you've been looking for?"
        body="Book a free 30-minute chat with Kim, or send an enquiry, every message lands directly with her."
      />
    </main>
  );
};

export default About;
